import { memo } from 'react';
import { Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { KeyRound, MailX, Trash2, UserCheck, UserX } from 'lucide-react';

import { PERMISSIONS } from '../../constants/permissions';
import Can from '../guards/Can';
import ReasonButton from './ReasonButton';

/**
 * Quick actions under the info card: Reset password, Suspend access and Remove from workspace
 * (red). A pending invitation also gets Revoke invitation (no confirmation: the toast offers Send again; neutral here, red in the row menu, as in the prototype) above the red one. Which ones apply,
 * and why one is disabled, comes from `getMemberActions`. The remove permission gates all of
 * them except Revoke, which needs the invite permission like Resend. Removed members get
 * Reactivate and Delete user instead.
 *
 * @param {object} props
 * @param {object} props.actions Result of `getMemberActions`.
 * @param {() => void} props.onResetPassword
 * @param {() => void} props.onSuspend
 * @param {() => void} props.onRemove
 * @param {() => void} props.onReactivate
 * @param {() => void} props.onRevoke
 * @param {() => void} props.onDelete
 */
function MemberQuickActions({ actions, onResetPassword, onSuspend, onRemove, onReactivate, onRevoke, onDelete }) {
  const { t } = useTranslation('people-access');
  return (
    <div className="flex flex-col gap-2" aria-label={t('memberDetail.quickActions.title')} role="group">
      <Can permission={PERMISSIONS.MEMBERS_REMOVE}>
        {actions.resetPassword ? (
          <Button variant="secondary" size="sm" leftIcon={KeyRound} onClick={onResetPassword} className="w-full">
            {t('memberDetail.quickActions.resetPassword')}
          </Button>
        ) : null}
        {actions.suspend.visible ? (
          <ReasonButton
            variant="secondary"
            size="sm"
            leftIcon={UserX}
            reason={actions.suspend.disabledReason}
            label={t('memberDetail.quickActions.suspend')}
            onClick={onSuspend}
          />
        ) : null}
        {actions.reactivate ? (
          <Button variant="secondary" size="sm" leftIcon={UserCheck} onClick={onReactivate} className="w-full">
            {t(actions.reactivateLabelKey)}
          </Button>
        ) : null}
      </Can>
      {actions.revoke ? (
        <Can permission={PERMISSIONS.MEMBERS_INVITE}>
          <Button variant="secondary" size="sm" leftIcon={MailX} onClick={onRevoke} className="w-full">
            {t('memberDetail.quickActions.revoke')}
          </Button>
        </Can>
      ) : null}
      <Can permission={PERMISSIONS.MEMBERS_REMOVE}>
        {actions.deleteUser.visible ? (
          <ReasonButton
            variant="danger"
            size="sm"
            leftIcon={Trash2}
            reason={actions.deleteUser.disabledReason}
            label={t('memberDetail.quickActions.deleteUser')}
            onClick={onDelete}
          />
        ) : null}
        {actions.remove.visible ? (
          <ReasonButton
            variant="danger"
            size="sm"
            leftIcon={Trash2}
            reason={actions.remove.disabledReason}
            label={t(actions.removeLabelKey)}
            onClick={onRemove}
          />
        ) : null}
      </Can>
    </div>
  );
}

export default memo(MemberQuickActions);
